/**
 * Agrupa episodios de series / anime_series en “shows” para UI tipo sagas.
 */

const SERIES_TYPES = new Set(["series", "anime_series"]);

export function isSeriesVideo(v) {
  return SERIES_TYPES.has(v?.content_type || "");
}

export function slugifySeries(name) {
  return String(name || "serie")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 80) || "serie";
}

function seriesKey(v) {
  if (v.tmdb_id) return `tmdb:${v.tmdb_id}`;
  const name = (v.series_title || "").trim().toLowerCase();
  if (name) return `title:${name}`;
  return `id:${v.id}`;
}

function sortEpisodes(a, b) {
  const sa = a.season_number ?? 0;
  const sb = b.season_number ?? 0;
  if (sa !== sb) return sa - sb;
  const ea = a.episode_number ?? 0;
  const eb = b.episode_number ?? 0;
  if (ea !== eb) return ea - eb;
  return String(a.title).localeCompare(String(b.title), "es");
}

/**
 * @param {Array} videos
 * @param {{ includeAnime?: boolean }} [opts]
 */
export function buildSeriesShows(videos, opts = {}) {
  const includeAnime = opts.includeAnime !== false;
  const list = (Array.isArray(videos) ? videos : []).filter((v) => {
    if (!isSeriesVideo(v)) return false;
    if (!includeAnime && v.content_type === "anime_series") return false;
    return true;
  });

  const map = new Map();
  for (const v of list) {
    const key = seriesKey(v);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(v);
  }

  const usedSlugs = new Set();
  const shows = [];

  for (const [key, eps] of map.entries()) {
    const items = [...eps].sort(sortEpisodes);
    const first = items[0];
    const title =
      (first.series_title || "").trim() ||
      String(first.title || "")
        .split(" · ")[0]
        .trim() ||
      `Serie #${first.id}`;

    let slug = slugifySeries(title);
    if (key.startsWith("tmdb:")) {
      slug = `${slug}-${String(key).replace("tmdb:", "")}`;
    }
    if (usedSlugs.has(slug)) slug = `${slug}-${first.id}`;
    usedSlugs.add(slug);

    const seasons = [
      ...new Set(
        items.map((e) => {
          const n = e.season_number;
          if (n == null || Number.isNaN(Number(n))) return 1;
          return Number(n);
        })
      ),
    ].sort((a, b) => a - b);

    const withCover =
      items.find((v) => v.cover_url) ||
      [...items].reverse().find((v) => v.cover_url) ||
      items[0];
    const seriesPoster =
      items.find((v) => v.series_poster_url)?.series_poster_url || null;
    const years = items.map((v) => v.year).filter(Boolean);

    /** @type {Record<number, string>} */
    const seasonPosters = {};
    for (const ep of items) {
      const sn =
        ep.season_number == null || Number.isNaN(Number(ep.season_number))
          ? 1
          : Number(ep.season_number);
      if (ep.season_poster_url && !seasonPosters[sn]) {
        seasonPosters[sn] = ep.season_poster_url;
      }
    }

    shows.push({
      slug,
      key,
      title,
      items,
      count: items.length,
      seasonCount: seasons.length || 1,
      seasons,
      seasonPosters,
      // Portada de la serie = póster TMDB; los capítulos usan frame en cover_url.
      cover_url: seriesPoster || withCover?.cover_url || null,
      // Banner: preferir backdrop estable de la serie (no el de un capítulo suelto).
      backdrop_url:
        items.find((v) => v.series_backdrop_url)?.series_backdrop_url ||
        items.find((v) => v.backdrop_url)?.backdrop_url ||
        seriesPoster ||
        withCover?.cover_url ||
        null,
      years,
      content_type: first.content_type || "series",
      tmdb_id: first.tmdb_id || null,
    });
  }

  return shows.sort(
    (a, b) => b.count - a.count || a.title.localeCompare(b.title, "es")
  );
}

export function findSeriesShow(videos, slug) {
  return buildSeriesShows(videos).find((s) => s.slug === slug) || null;
}

/** Ítem de catálogo (fila/grilla) a partir de un show agrupado. */
export function showToCatalogItem(show) {
  const first = show.items?.[0] || {};
  const years = Array.isArray(show.years) ? show.years.filter(Boolean) : [];
  const ratings = (show.items || [])
    .map((v) => Number(v.rating_avg))
    .filter((n) => n > 0);
  const rating_avg = ratings.length
    ? Math.round((ratings.reduce((a, b) => a + b, 0) / ratings.length) * 10) / 10
    : first.rating_avg || null;

  return {
    id: first.id,
    catalog_key: show.slug,
    title: show.title,
    cover_url: show.cover_url,
    backdrop_url: show.backdrop_url,
    category: (first.category || "").trim() || "Sin categoría",
    year: years.length ? Math.max(...years.map(Number)) : first.year || null,
    content_type: show.content_type || first.content_type || "series",
    rating_avg,
    is_favorite: (show.items || []).some((v) => v.is_favorite),
    catalog_href: `/series/${show.slug}`,
    created_at: first.created_at,
  };
}
